import axios from "axios";

export const getQuoteRequests = async ({ query = {}, controller = null, excludeInterceptor = false }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get("/quotes/requests", params);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getQuoteRequest = async ({ data, controller = null}: any) => {	
	try {
		const res: any = await axios.get(`/quotes/requests/${data?.id}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const updateQuoteRequest = async ({ data, update }: any) => {
	try {
		const res: any = await axios.put(`/quotes/requests/${data?.id}`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const generateQuoteFromEstimate = async ({ data, service }: any) => {
	try {
		const res: any = await axios.post(`/estimates/${data.id}/generate-quote`, service);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

// RFQ pricing-verification pivot (OPERATING_MODEL §3 Step 3). Staff-only
// read of the provider/depot pricing alternatives for one estimate
// service. Backs the "Change provider" picker on the RFQ Items surface.
export const getProviderAlternatives = async ({ serviceId }: any) => {
	try {
		const res: any = await axios.get(`/estimates/services/${serviceId}/provider-alternatives`);
		return res?.data ? res.data : { current: null, alternatives: [], unprovisioned: [] };
	} catch (e: any) {
		return { current: null, alternatives: [], unprovisioned: [] };
	}
};

// Maintenance context for an RFQ that a Work Order converted into.
// Returns null (as {}) for a plain RFQ. Drives the context banner +
// live NTE delta on the RFQ surface.
export const getRfqMaintenanceContext = async ({ rfqId }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/by-rfq/${rfqId}/context`);
		return res?.data ? res.data : null;
	} catch (e: any) {
		return null;
	}
};

// CLIENT-safe maintenance context for a quote's RFQ. Drives the "back
// to your work order" banner on the trunk quote page, which is otherwise
// a dead end for a client who arrived from their work order. Returns
// null for a plain RFQ or one the caller's org does not own, so the
// banner simply does not render.
export const getClientWorkOrderContextForRfq = async ({ rfqId }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/by-rfq/${rfqId}/client-context`);
		return res?.data ? res.data : null;
	} catch (e: any) {
		return null;
	}
};

// Project-page Update Client gate. Given a project's originating RFQ id,
// reports whether a Corrigo-managed (work_order_fed) work order produced
// it, so the project view can decide whether to show the Update Client
// (CorrigoPro status paste) button. Returns
// { hasCorrigoWorkOrder, workOrderId }; falls back to a safe negative on
// any error so the button simply stays hidden.
export const getCorrigoWorkOrderForRfq = async ({ rfqId }: any) => {
	try {
		const res: any = await axios.get(`/maintenance/work-orders/by-rfq/${rfqId}/corrigo-wo`);
		return res?.data ? res.data : { hasCorrigoWorkOrder: false, workOrderId: null, servicePlanId: null };
	} catch (e: any) {
		return { hasCorrigoWorkOrder: false, workOrderId: null, servicePlanId: null };
	}
};


/* ---- Duplicate RFQ cleanup (staff) ---------------------------------- */
export interface RfqDuplicateMember {
	id: string;
	sequence: number | null;
	status: number;
	createdAt: string;
	projectName: string | null;
	requester: string | null;
	quantity: number | null;
	noteCount: number;
}
export interface RfqDuplicateGroup {
	key: string;
	orgName: string;
	siteName: string | null;
	siteCity: string | null;
	products: string | null;
	sameQuantity: boolean;
	members: RfqDuplicateMember[];
}

const dupFail = (e: any, what: string) => ({ error: e?.response?.data?.message ? String(e.response.data.message) : `${what} failed.` });

export const getRfqDuplicates = async (): Promise<{ groups: RfqDuplicateGroup[]; total: number; extra: number; reviewed?: RfqDuplicateGroup[] } | { error: string }> => {
	try {
		const res = await axios.get(`/quotes/requests/duplicates`);
		return res.data;
	} catch (e: any) {
		return dupFail(e, "Loading duplicate RFQs");
	}
};

export const mergeRfqDuplicates = async (survivorId: string, mergeIds: string[]): Promise<{ kept: string; merged: string[] } | { error: string }> => {
	try {
		const res = await axios.post(`/quotes/requests/duplicates/merge`, { survivorId, mergeIds });
		return res.data;
	} catch (e: any) {
		return dupFail(e, "Merging RFQs");
	}
};

/** Put a reviewed set back on the Duplicates list. */
export const reopenRfqDuplicates = async (ids: string[]): Promise<{ reopened: number } | { error: string }> => {
	try {
		const res = await axios.post(`/quotes/requests/duplicates/reopen`, { ids });
		return res.data;
	} catch (e: any) {
		return dupFail(e, "Saving");
	}
};

export const dismissRfqDuplicates = async (ids: string[]): Promise<{ dismissed: number } | { error: string }> => {
	try {
		const res = await axios.post(`/quotes/requests/duplicates/dismiss`, { ids });
		return res.data;
	} catch (e: any) {
		return dupFail(e, "Saving");
	}
};
